import type { ChamberAnalysisRequestActionInterface } from 'store/chamberAnalysis/actions';
import { setChamberAnalysisResult } from 'store/chamberAnalysis/actions';
import type {
  ChamberAnalysisRequestInterface,
  ChamberAnalysisResponseInterface,
} from 'store/chamberAnalysis/types';
import { CHAMBER_ANALYSIS } from 'store/chamberAnalysis/types';
import { chamberAnalysis } from 'api/simulation/simulateApi';
import { put, select, takeLatest } from 'redux-saga/effects';
import * as Sentry from '@sentry/react';
import type { HttpResponseInterface } from 'api/apiClient';
import { withRetry } from 'store/sagaUtils';
import { addMessage } from 'store/snackbar/reducer';
import { errorMessage } from 'containers/Snackbar/snackbarMessages';
import {
  setFailedChamberAnalysisSimulation,
  setRunningChamberAnalysisSimulation,
} from 'store/layout/reducer';
import { getIsOnline } from 'store/connection/selectors';

function isAnalysisResponse(
  body: unknown,
): body is ChamberAnalysisResponseInterface {
  return typeof body === 'object' && body !== null && 'units' in body;
}

function* chamberAnalysisSaga(action: ChamberAnalysisRequestActionInterface) {
  const isOnline: boolean = yield select(getIsOnline);
  if (!isOnline) {
    return;
  }

  const unitId = action.payload.key;
  const request: ChamberAnalysisRequestInterface = {
    units: [{ key: unitId, unitInfo: action.payload.chamberUnitInfo }],
  };

  let failed = false;

  try {
    yield put(setRunningChamberAnalysisSimulation({ unitId, running: true }));
    yield put(setFailedChamberAnalysisSimulation({ unitId, failed: false }));

    const result: HttpResponseInterface<ChamberAnalysisResponseInterface> =
      yield withRetry(() => chamberAnalysis(request));
    const body = result.parsedBody;

    if (result.ok && isAnalysisResponse(body)) {
      yield put(setChamberAnalysisResult(body));
      const unitError = body.units.length === 1 && body.units[0]?.error;
      if (unitError) {
        failed = true;
        yield put(
          addMessage(errorMessage(`Chamber analysis failed - ${unitError}`)),
        );
      }
    } else {
      failed = true;
      yield put(addMessage(errorMessage('Chamber analysis failed')));
    }
  } catch (e) {
    failed = true;
    Sentry.captureException(e, { tags: { saga: 'chamberAnalysis' } });
    yield put(addMessage(errorMessage('Chamber analysis failed')));
  } finally {
    yield put(setFailedChamberAnalysisSimulation({ unitId, failed }));
    yield put(setRunningChamberAnalysisSimulation({ unitId, running: false }));
  }
}

function* chamberAnalysisSagas() {
  yield takeLatest(CHAMBER_ANALYSIS, chamberAnalysisSaga); // TODO: Change to 'takeEvery' because multiple units can be simulated at the same time?
}

export default chamberAnalysisSagas;
